import { NgIf, AsyncPipe, NgSwitch, NgSwitchCase } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FreshdeskSolutionService } from '@asamco/application-link/shared';
import { BehaviorSubject, distinctUntilChanged, shareReplay, Subject, Observable, exhaustMap, of, map, catchError, startWith, timer, take, tap } from 'rxjs';

type SupportDiagnostics = {
  url?: string;
  userAgent?: string;
  timezone?: string;
  locale?: string;
  timestampIso?: string;
};

type HelpOrigin =
  | {
      kind: 'link_logs';
      linkId: number;
      instanceId?: string | null;
      jobLogType?: number | string | null;
      jobLogId?: number | string | null;
      logMessage?: string | null;
      pattern?: { id?: number | string | null };
    }
  | { kind: 'unknown' };

type SupportPayload = {
  articleId: number | null;
  rating: number | null;
  reason: string;
  details: string;
  email?: string | null;
  origin?: HelpOrigin;
  missingArticleForError?: boolean; // matches your backend flag
};

type UiState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'sent' }
  | { kind: 'error'; message: string };

@Component({
  selector: 'app-missing-article-feedback',
  standalone: true,
  imports: [NgIf, NgSwitch, NgSwitchCase, AsyncPipe, FormsModule],
  templateUrl: './missing-article-feedback.component.html',
  styleUrls: ['../support-styles.scss', './missing-article-feedback.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MissingArticleFeedbackComponent {
  @Input() backdrop = true;
  @Input() title = 'Request help';
  @Output() sent = new EventEmitter<void>();

  private readonly visibleSubject = new BehaviorSubject<boolean>(false);
  readonly visible$ = this.visibleSubject.asObservable().pipe(distinctUntilChanged(), shareReplay(1));

  private origin: HelpOrigin = { kind: 'unknown' };

  email: string | null = null;
  details = '';

  private readonly submitClick$ = new Subject<void>();

  readonly uiState$: Observable<UiState> = this.submitClick$.pipe(
    exhaustMap(() => {
      const err = this.validate();
      if (err) return of<UiState>({ kind: 'error', message: err });

      const payload = this.buildPayload();

      return this.freshdeskSolutionService.submitSupport(payload).pipe(
        map((): UiState => ({ kind: 'sent' })),
        catchError(e => {
          console.error('MissingArticleFeedback submit failed', e);
          return of<UiState>({ kind: 'error', message: 'Couldn’t send. Please try again.' });
        }),
        startWith<UiState>({ kind: 'submitting' })
      );
    }),
    startWith<UiState>({ kind: 'idle' }),
    shareReplay({ bufferSize: 1, refCount: true })
  );


  readonly submitting$ = this.uiState$.pipe(map(s => s.kind === 'submitting'), distinctUntilChanged());
  readonly sent$ = this.uiState$.pipe(map(s => s.kind === 'sent'), distinctUntilChanged());
  readonly error$ = this.uiState$.pipe(map(s => s.kind === 'error'), distinctUntilChanged());
  readonly errorMessage$ = this.uiState$.pipe(map(s => (s.kind === 'error' ? s.message : null)), distinctUntilChanged());

 private readonly autoCloseSeconds = 8;

readonly autoCloseIn$ = this.sent$.pipe(
  exhaustMap(isSent => {
    if (!isSent) return of<number | null>(null);

    return timer(0, 1000).pipe(
      map(t => this.autoCloseSeconds - t),
      take(this.autoCloseSeconds + 1),
      tap(sec => {
        if (sec === 0) {
          this.close();
          // not strictly required, but safe with OnPush
          this.cdr.markForCheck();
        }
      })
    );
  }),
  shareReplay({ bufferSize: 1, refCount: true })
);


  constructor(
    private readonly freshdeskSolutionService: FreshdeskSolutionService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  open(origin: HelpOrigin) {
    this.origin = origin ?? { kind: 'unknown' };

    // reset form each open
    this.email = null;
    this.details = '';

    this.visibleSubject.next(true);
    this.cdr.detectChanges();
  }

  close() {
    this.visibleSubject.next(false);
  }

  submit() {
    this.submitClick$.next();
  }

  private validate(): string | null {
    const d = (this.details ?? '').trim();
    if (!d) return 'Please describe what happened and what you expected.';

    const e = (this.email ?? '').trim();
    if (e && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) {
      return 'Please enter a valid email address (or leave it blank).';
    }

    return null;
  }

  private buildPayload(): SupportPayload {
    const email = (this.email ?? '').trim() || null;

    return {
      articleId: null,
      rating: null,
      reason: 'Missing help article for this error',
      details: (this.details ?? '').trim(),
      email,
      origin: this.origin,
      missingArticleForError: true,
    };
  }

  private buildDiagnostics(): SupportDiagnostics {
    return {
      url: typeof window !== 'undefined' ? window.location?.href : undefined,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      timezone: Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone,
      locale: typeof navigator !== 'undefined' ? navigator.language : undefined,
      timestampIso: new Date().toISOString(),
    };
  }
}
